import type { ReactNode } from "react";
import { AlertTriangle, ChevronDown, Clock, Info, Sparkles } from "lucide-react";
import {
  DAY_END_MIN,
  formatDay,
  formatTime,
  formatTimeRange,
  parseDayKey,
} from "@/lib/calendar";
import type { Interval, Slot } from "@/lib/calendar-slots";
import { cn } from "@/lib/utils";

export const slotKey = (s: Pick<Slot, "day" | "start">) => `${s.day}@${s.start}`;

function Radio({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] bg-white",
        on ? "border-primary" : "border-foreground/35",
      )}
    >
      {on && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
    </span>
  );
}

export function SlotTag({ kind }: { kind: "best" | "pick" | "overlaps" }) {
  if (kind === "overlaps")
    return <span className="rounded-full bg-[#fbf1d6] px-2 py-0.5 text-[11.5px] font-medium text-[#86621a] ring-1 ring-inset ring-[#ecd28c]">Overlaps</span>;
  if (kind === "pick") return <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11.5px] font-medium text-primary">Your pick</span>;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11.5px] font-medium text-primary">
      <Sparkles className="h-3 w-3" />
      Best fit
    </span>
  );
}

/** The options a time picker offers: quarter hours from 6 AM (or `minMinutes`), ending by 10 PM. */
export function timeOptions(durationMin: number, minMinutes = 6 * 60): number[] {
  const out: number[] = [];
  for (let m = Math.max(6 * 60, minMinutes); m + durationMin <= 22 * 60; m += 15) out.push(m);
  return out;
}

/** A pill that reads like the design but is a native select underneath (keyboard and phones for free). */
export function TimeSelect({
  value,
  onChange,
  durationMin,
  label,
  className,
  minMinutes,
}: {
  value: number;
  onChange: (m: number) => void;
  durationMin: number;
  label: string;
  className?: string;
  /** Earliest start offered — on today, nothing that has already passed. */
  minMinutes?: number;
}) {
  return (
    <span
      className={cn(
        "relative inline-flex h-10 items-center gap-2 rounded-[12px] border border-foreground/[0.12] bg-white px-3 text-[14px] text-foreground focus-within:ring-2 focus-within:ring-primary/40",
        className,
      )}
    >
      <Clock className="h-4 w-4 text-foreground/60" />
      <span className="tabular-nums">{formatTime(value)}</span>
      <ChevronDown className="ml-auto h-3.5 w-3.5 text-foreground/50" />
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {timeOptions(durationMin, minMinutes).map((m) => (
          <option key={m} value={m}>
            {formatTime(m)}
          </option>
        ))}
      </select>
    </span>
  );
}

export interface OwnTime {
  open: boolean;
  start: number;
  selected: boolean;
  clash: Interval | null;
  nextFree: number | null;
  /** The person chose to keep an overlapping time. */
  kept: boolean;
}

/**
 * Free times for the picked day (or common times without free/busy, or the nearest
 * times when the day is full), plus "their own time" one click away — which warns
 * about a clash but never blocks it.
 */
export function SlotList({
  heading,
  hint,
  loading,
  notice,
  slots,
  selectedKey,
  pickedDay,
  onSelect,
  own,
  durationMin,
  onOpenOwn,
  onOwnStart,
  onSelectOwn,
  onUseNextFree,
  onKeepOwn,
  chooseLabel,
  minMinutes,
}: {
  heading: string;
  hint: string;
  loading: boolean;
  notice?: ReactNode;
  slots: Slot[];
  selectedKey: string | null;
  /** The day picked on the month; slots on other days say their date. */
  pickedDay: string;
  onSelect: (s: Slot) => void;
  own: OwnTime;
  durationMin: number;
  onOpenOwn: () => void;
  onOwnStart: (m: number) => void;
  onSelectOwn: () => void;
  onUseNextFree: () => void;
  onKeepOwn: () => void;
  chooseLabel: string;
  /** Earliest own time on the picked day (later than 6 AM on today). */
  minMinutes?: number;
}) {
  const bestKey = slots.find((s) => s.tag === "best");
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[15px] font-semibold text-foreground">{heading}</h2>
        <p className="shrink-0 text-xs text-muted-foreground">{hint}</p>
      </div>
      {notice && (
        <p className="mt-3 flex gap-2.5 rounded-[14px] bg-primary/[0.07] px-3.5 py-3 text-[13px] leading-relaxed text-foreground/80">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>{notice}</span>
        </p>
      )}

      <div role="radiogroup" aria-label={heading} className="mt-3 space-y-2.5">
        {loading
          ? Array.from({ length: 3 }, (_, i) => (
              <div key={i} aria-hidden className="flex items-center gap-3.5 rounded-[14px] border border-foreground/[0.08] px-4 py-4">
                <span className="h-[18px] w-[18px] animate-pulse rounded-full bg-foreground/[0.06]" />
                <span className="flex-1 space-y-2">
                  <span className="block h-3 w-36 animate-pulse rounded-full bg-foreground/[0.07]" />
                  <span className="block h-2.5 w-full max-w-[300px] animate-pulse rounded-full bg-foreground/[0.05]" />
                </span>
              </div>
            ))
          : slots.map((s) => {
              const key = slotKey(s);
              const on = key === selectedKey && !own.selected;
              const tag = s.tag === "best" ? "best" : s.tag === "overlaps" ? "overlaps" : on && bestKey ? "pick" : null;
              const otherDay = s.day !== pickedDay;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onSelect(s)}
                  className={cn(
                    "flex w-full items-center gap-3.5 rounded-[14px] border px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    on
                      ? "border-primary/55 bg-primary/[0.05] shadow-[0_0_0_3px_rgba(110,83,172,0.07)]"
                      : "border-foreground/[0.1] hover:border-foreground/20",
                  )}
                >
                  <Radio on={on} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[16px] font-medium tabular-nums text-foreground">
                        {otherDay && `${formatDay(parseDayKey(s.day))} · `}
                        {formatTimeRange(s.start, s.end)}
                      </span>
                      {tag && <SlotTag kind={tag} />}
                    </span>
                    <span className="mt-0.5 block text-[12.5px] leading-snug text-muted-foreground">{s.reason}</span>
                  </span>
                </button>
              );
            })}

        {own.open && (
          <div
            className={cn(
              "rounded-[14px] border px-4 py-3.5 transition-colors",
              own.selected && own.clash && !own.kept
                ? "border-[#e0868d] bg-white shadow-[0_0_0_3px_rgba(217,84,94,0.08)]"
                : own.selected
                  ? "border-primary/55 bg-primary/[0.05] shadow-[0_0_0_3px_rgba(110,83,172,0.07)]"
                  : "border-foreground/[0.1]",
            )}
          >
            <div className="flex items-center gap-3.5">
              <button
                type="button"
                role="radio"
                aria-checked={own.selected}
                onClick={onSelectOwn}
                className="flex flex-1 items-center gap-3.5 text-left focus-visible:outline-none"
              >
                <Radio on={own.selected} />
                <span className="text-[16px] font-medium text-foreground">Your own time</span>
              </button>
              <TimeSelect
                label="Your own start time"
                value={own.start}
                onChange={(m) => {
                  onOwnStart(m);
                  onSelectOwn();
                }}
                durationMin={durationMin}
                minMinutes={minMinutes}
                className={cn(own.selected && "border-primary/50")}
              />
            </div>
            {own.selected && own.clash && !own.kept && (
              <div role="alert" className="mt-3 rounded-xl bg-[#fdecec] px-3.5 py-3">
                <p className="flex items-center gap-2 text-[13.5px] text-[#a8323e]">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  This overlaps a busy time, {formatTimeRange(own.clash.start, own.clash.end)}.
                </p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {own.nextFree !== null && own.nextFree + durationMin <= DAY_END_MIN && (
                    <button
                      type="button"
                      onClick={onUseNextFree}
                      className="rounded-full bg-foreground px-3 py-1.5 text-[13px] font-medium text-white hover:bg-foreground/90"
                    >
                      Use {formatTime(own.nextFree)}, the next free time
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onKeepOwn}
                    className="rounded-full border border-foreground/15 bg-white px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-foreground/[0.03]"
                  >
                    Keep {formatTime(own.start)}
                  </button>
                </div>
              </div>
            )}
            {own.selected && own.clash && own.kept && (
              <p className="mt-2 pl-8 text-[12.5px] text-[#a8323e]">
                Overlaps {formatTimeRange(own.clash.start, own.clash.end)} — you&apos;re keeping it.
              </p>
            )}
          </div>
        )}
      </div>

      {!own.open && !loading && (
        <button
          type="button"
          onClick={onOpenOwn}
          className="mt-3.5 inline-flex items-center gap-1.5 text-[14px] font-medium text-primary underline-offset-4 hover:underline"
        >
          <Clock className="h-4 w-4" />
          {chooseLabel}
        </button>
      )}
    </div>
  );
}
